import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ProfileLink, ProfileLinks as ProfileLinksComponent } from 'us-react-components/dist/ProfileLinks/ProfileLinks';
import { Storage } from '../../../constants';
import { useDependencies } from '../../../dependencies';
import { getUrlFromPath, Routes } from '../../../Routes';

const ProfileLinks = (): JSX.Element => {
  const { services: { config, tracker }, stores } = useDependencies();
  const location = useLocation();
  const { t } = useTranslation();
  const id = stores.user.user.id?.toString() ?? '';

  const handleFeedbackClick = (): void => {
    localStorage.setItem(Storage.NPS_FEEDBACK_SOURCE, location.pathname);
  };

  const handleLogoutClick = (): void => {
    stores.user.logout();
  };

  const profileLinksParent: ProfileLink[] = [
    {
      link: getUrlFromPath(Routes.parent.yourSitters, { id }),
      trackingTag: 'profile_links_your_caregivers',
      label: t('components.header.profile.yourCaregivers'),
      classes: 'font-bold',
      isLogOut: false,
    },
    {
      link: getUrlFromPath(Routes.parent.profile, { id }),
      trackingTag: 'profile_links_profile',
      label: t('components.header.profile.profile'),
      classes: 'font-bold',
      hasHR: true,
    },
    {
      link: getUrlFromPath(Routes.parent.settings, { id }),
      trackingTag: 'profile_links_settings',
      label: t('components.header.profile.settings'),
    },
    {
      link: getUrlFromPath(Routes.credits, { id }),
      trackingTag: 'profile_links_credits',
      label: t('components.header.profile.credits'),
    },
    {
      link: getUrlFromPath(Routes.creditCard),
      trackingTag: 'profile_links_payment',
      label: t('components.header.profile.payment'),
    },
    {
      link: getUrlFromPath(Routes.transactions),
      trackingTag: 'profile_links_transactions',
      label: t('components.header.profile.transactions'),
    },
    {
      link: getUrlFromPath(Routes.referral),
      trackingTag: 'profile_links_invite_friends',
      label: t('components.header.profile.inviteFriends'),
    },
    {
      link: getUrlFromPath(Routes.feedback),
      trackingTag: 'profile_links_give_feedback',
      label: t('components.header.profile.giveFeedback'),
      hasHR: true,
      isFeedback: true,
    },
  ];

  if (stores.parent.hasCariloopPartnership && config.CARILOOP_URL.length) {
    profileLinksParent.push({
      link: config.CARILOOP_URL,
      trackingTag: 'profile_links_back_to_cariloop',
      label: t('components.header.profile.backToCariloop'),
      classes: 'text-sapphire font-bold',
      isLogOut: false,
    });
  }

  profileLinksParent.push({
    trackingTag: 'profile_links_logout',
    label: t('components.header.profile.logout'),
    classes: 'text-sapphire font-bold',
    isLogOut: true,
  });

  const profileLinksSitter = [
    {
      link: getUrlFromPath(Routes.sitter.yourFamilies, { id }),
      trackingTag: 'profile_links_your_families',
      label: t('components.header.profile.yourFamilies'),
      classes: 'font-bold',
    },
    {
      link: getUrlFromPath(Routes.sitter.settings, { id }),
      trackingTag: 'profile_links_settings',
      label: t('components.header.profile.settings'),
      classes: 'font-bold',
      hasHR: true,
    },
    {
      link: getUrlFromPath(Routes.credits, { id }),
      trackingTag: 'profile_links_credits',
      label: t('components.header.profile.credits'),
    },
    {
      link: getUrlFromPath(Routes.sitter.payout),
      trackingTag: 'profile_links_payout',
      label: t('components.header.profile.payout'),
    },
    {
      link: getUrlFromPath(Routes.transactions),
      trackingTag: 'profile_links_transactions',
      label: t('components.header.profile.transactions'),
    },
    {
      link: getUrlFromPath(Routes.sitter.successGuide),
      trackingTag: 'profile_links_success_guide',
      label: t('components.header.profile.successGuide'),
    },
    {
      link: getUrlFromPath(Routes.feedback),
      trackingTag: 'profile_links_give_feedback',
      label: t('components.header.profile.giveFeedback'),
      hasHR: true,
      isFeedback: true,
    },
    {
      trackingTag: 'profile_links_logout',
      label: t('components.header.profile.logout'),
      classes: 'text-sapphire font-bold',
      isLogOut: true,
    },
  ];

  const profileLinksArray = stores.user.user!.parent ? profileLinksParent : profileLinksSitter;

  const trackEvent = async (event: string, params?: {elementText: string}): Promise<void> => tracker.track(event, params);

  return <ProfileLinksComponent
    profileLinksArray={profileLinksArray}
    handleFeedbackClick={handleFeedbackClick}
    handleLogoutClick={handleLogoutClick}
    trackingCallback={trackEvent}
  />
};

export { ProfileLinks };
